import { WorkfrontBundleObjectRole } from '@atlas/types'
import { Badge } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { WorkfrontBundleNodeDTO } from '@/services/workfront.service'
import { humanize } from './StatusBadges'

export interface BundleTreeProps {
	nodes: WorkfrontBundleNodeDTO[]
}

/** Every object under a node, at any depth: what a collapsed node hides. */
function descendantCount(node: WorkfrontBundleNodeDTO): number {
	return node.children.reduce((total, child) => total + 1 + descendantCount(child), 0)
}

/**
 * One object of the bundle and what hangs under it: its role, name, status, a
 * short fingerprint and the version it last changed in. A removed object stays,
 * struck through, so the tree shows what left as well as what is there. An asset
 * brief starts collapsed: a large bundle holds hundreds of them, with thousands
 * of tasks between them.
 */
export function BundleTreeNode({ node }: { node: WorkfrontBundleNodeDTO }) {
	const [open, setOpen] = useState(node.role !== WorkfrontBundleObjectRole.CHILD)
	const hasChildren = node.children.length > 0
	return (
		<li>
			<div className="flex items-center gap-2 py-1 text-ds-sm">
				{hasChildren ? (
					<button
						type="button"
						aria-expanded={open}
						aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name ?? node.workfrontId}`}
						onClick={() => setOpen((current) => !current)}
						className="w-4 cursor-pointer text-ds-text-muted"
					>
						{open ? '▾' : '▸'}
					</button>
				) : (
					<span className="w-4" />
				)}
				<Badge size="sm" variant="muted">
					{humanize(node.role)}
				</Badge>
				<span className={node.removedAt ? 'text-ds-text-muted line-through' : 'font-medium'}>
					{node.name ?? node.workfrontId}
				</span>
				{node.status ? <span className="text-ds-text-muted">{node.status}</span> : null}
				{hasChildren && !open ? <span className="text-ds-text-muted">({descendantCount(node)})</span> : null}
				<span className="ml-auto flex items-center gap-3 font-mono text-ds-xs text-ds-text-muted">
					<span title="Fingerprint">{node.shortSha}</span>
					<span title="Last changed in version">v{node.changedInVersion}</span>
				</span>
			</div>
			{hasChildren && open ? (
				<ul className="ms-2 border-ds-border border-l ps-4">
					{node.children.map((child) => (
						<BundleTreeNode key={child.workfrontId} node={child} />
					))}
				</ul>
			) : null}
		</li>
	)
}

/** A bundle as a tree: its projects, with their issues, tasks, notes and documents under them. */
export function BundleTree({ nodes }: BundleTreeProps) {
	if (nodes.length === 0) return <p className="text-ds-sm text-ds-text-muted">Nothing fetched yet.</p>
	return (
		<ul aria-label="Bundle tree">
			{nodes.map((node) => (
				<BundleTreeNode key={node.workfrontId} node={node} />
			))}
		</ul>
	)
}
